import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AssetDto, FolderDto, GalleryDto } from '@nusign/picflow-backend-types';
import { filter, firstValueFrom, from, startWith, switchMap, withLatestFrom } from 'rxjs';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { getFormControl } from '../../../lib/get-form-control';
import { IsGallery } from '../../../lib/is-gallery';
import { sleep } from '../../../lib/sleep';
import { FolderService } from '../../../services/folder.service';
import { GalleryService } from '../../../services/gallery.service';
import { ShareService } from '../../../services/share.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { ShareDirective } from '../../../shared-extra/directives/share.directive';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';

@Component({
  selector: 'app-editable-gallery-url-field',
  templateUrl: './editable-gallery-url-field.component.html',
  styleUrls: ['./editable-gallery-url-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputFieldComponent,
    TooltipDirective,
    InputPostDirective,
    ButtonDirective,
    ShareDirective,
    FormsModule,
    ReactiveFormsModule,
    LoadingIndicatorComponent,
    AutofocusDirective,
    SvgComponent,
  ],
})
export class EditableGalleryUrlFieldComponent {
  private state = inject(StateService);
  private shareService = inject(ShareService);
  private galleryService = inject(GalleryService);
  private folderService = inject(FolderService);
  private toasts = inject(ToastService);
  readonly item = input<GalleryDto | FolderDto>(undefined);
  readonly asset = input<AssetDto>(undefined);
  readonly showCopyButton = input(true);
  readonly showEditButton = input(false);
  readonly galleryPermissions = input<GalleryDto['permissions']>(undefined);
  readonly folderPermissions = input<FolderDto['permissions']>(undefined);
  folderOrGalleryPermissions = computed(() => {
    const galleryPermissions = this.galleryPermissions();
    const folderPermissions = this.folderPermissions();
    return galleryPermissions || folderPermissions;
  });
  onEditingPathChange = output<boolean>();
  canShare = navigator.share && ScreenSizes.screenSize() !== 'desktop';
  pathForm = new FormGroup({
    path: getFormControl('galleryPath'),
  });

  link = toSignal(
    toObservable(this.item).pipe(
      filter(Boolean),
      withLatestFrom(toObservable(this.asset).pipe(startWith(undefined))),
      switchMap(([item, asset]) => {
        if (IsGallery(item)) {
          return from(this.shareService.getShareLink(item as GalleryDto, { assetId: asset?.id }));
        }
        return from(this.shareService.getFolderShareLink(this.item() as FolderDto));
      })
    )
  );
  publicLink = computed(() => this.link()?.url);
  publicLinkPre = computed(() => this.link()?.pre);
  publicLinkPost = computed(() => this.link()?.post);
  editingPath = signal(false);
  savingPath = signal(false);
  activeTenant = toSignal(this.state.activeTenant$);

  async startEditPath() {
    const item = this.item();
    if (
      item.public_access === 'no_access' ||
      (item.tenant || this.state.activeTenant$.value)['block_public_link_access']
    ) {
      return;
    }
    if (!this.folderOrGalleryPermissions().can_edit) return;
    await sleep(0); // needed otherwise context closes
    this.pathForm.controls.path.setValue(this.item().path);
    this.editingPath.set(true);
    this.onEditingPathChange.emit(true);
  }

  async cancelEditPath() {
    await sleep(0); // needed otherwise context closes
    this.editingPath.set(false);
    this.onEditingPathChange.emit(false);
  }

  async savePath() {
    await sleep(0);
    if (!this.pathForm.valid) return;
    if (this.savingPath()) return;
    const path = this.pathForm.value.path;
    const item = this.item();
    if (!path || path === item.path) {
      await sleep(0); // needed otherwise context closes
      this.editingPath.set(false);
      this.onEditingPathChange.emit(false);
      return;
    }
    this.savingPath.set(true);
    try {
      if (IsGallery(this.item())) {
        await this.galleryService.updateGallery(item.id, { path });
      } else {
        await firstValueFrom(this.folderService.updateFolder(item.id, { path }));
      }
      this.editingPath.set(false);
      this.onEditingPathChange.emit(false);
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.error.code !== 'conflict') throw err;
      this.toasts.add('This link already exists!', 'error');
    }
    this.savingPath.set(false);
  }

  onCopied() {
    this.toasts.add('Link Copied', 'success');
  }
}
